import React, { useEffect, useState, createElement } from 'react';
import { DollarSignIcon, DownloadIcon, PlusIcon, EditIcon, TrashIcon, TrendingUpIcon, CalendarIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { getPayments, calculatePaymentKPIs, deletePayment, type Payment } from '../../services/paymentService';
import { getTeacherStudents } from '../../services/studentService';
import { AddPaymentModal } from '../modals/AddPaymentModal';
import { Timestamp } from 'firebase/firestore';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
// Stat card component
const StatCard = ({
  icon,
  label,
  value,
  darkMode
}) => {
  return <div className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-6836">
      <div className="flex justify-between items-start" data-id="element-6837">
        <div data-id="element-6838">
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6839">
            {label}
          </p>
          <h3 className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-6840">
            {value}
          </h3>
        </div>
        <div className={`p-3 rounded-full ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-6841">
          {icon}
        </div>
      </div>
    </div>;
};
export const RevenuePage = () => {
  const {
    theme
  } = useTheme();
  const {
    currentUser
  } = useAuth();
  const isDark = theme === 'dark';
  // UI state
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  // Data state
  const [payments, setPayments] = useState<Payment[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // Filter states
  const [selectedStudent, setSelectedStudent] = useState('All');
  const [dateRange, setDateRange] = useState<'This month' | 'Last 30 days' | 'Year to date'>('This month');
  // Fetch students
  useEffect(() => {
    const loadStudents = async () => {
      if (!currentUser?.uid) return;
      try {
        const fetchedStudents = await getTeacherStudents(currentUser.uid);
        setStudents(fetchedStudents);
      } catch (error) {
        console.error('Error loading students:', error);
      }
    };
    loadStudents();
  }, [currentUser?.uid]);
  // Fetch payments
  const loadPayments = async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // Calculate date range
      const now = new Date();
      let fromDate: Date | undefined;
      let toDate: Date | undefined = now;
      if (dateRange === 'This month') {
        fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
      } else if (dateRange === 'Last 30 days') {
        fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      } else if (dateRange === 'Year to date') {
        fromDate = new Date(now.getFullYear(), 0, 1);
      }
      const filters = {
        studentId: selectedStudent,
        fromDate,
        toDate
      };
      const fetchedPayments = await getPayments(currentUser.uid, filters);
      setPayments(fetchedPayments);
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadPayments();
  }, [currentUser?.uid, selectedStudent, dateRange]);
  // Calculate KPIs
  const thisMonthKPI = calculatePaymentKPIs(payments, 'thisMonth');
  const last30DaysKPI = calculatePaymentKPIs(payments, 'last30Days');
  const yearToDateKPI = calculatePaymentKPIs(payments, 'yearToDate');
  // Calculate revenue by month for chart
  const calculateRevenueByMonth = () => {
    const monthData: {
      [key: string]: number;
    } = {};
    payments.forEach(payment => {
      const paidAt = payment.paidAt instanceof Timestamp ? payment.paidAt.toDate() : new Date(payment.paidAt);
      const monthKey = paidAt.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
      monthData[monthKey] = (monthData[monthKey] || 0) + payment.amount;
    });
    const chartData = Object.entries(monthData).map(([month, revenue]) => ({
      month,
      revenue: Math.round(revenue * 100) / 100
    })).slice(-6); // Last 6 months
    // For single data point, add a baseline point at zero
    if (chartData.length === 1) {
      return [{
        month: '',
        revenue: 0
      }, chartData[0]];
    }
    return chartData;
  };
  const revenueMonthlyData = calculateRevenueByMonth();
  // Handle delete payment
  const handleDeletePayment = async (paymentId: string) => {
    if (!confirm('Are you sure you want to delete this payment? This action cannot be undone.')) {
      return;
    }
    try {
      await deletePayment(currentUser!.uid, paymentId);
      await loadPayments(); // Reload data
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Failed to delete payment. Please try again.');
    }
  };
  // Handle edit payment
  const handleEditPayment = (payment: Payment) => {
    setEditingPayment(payment);
    setIsAddPaymentModalOpen(true);
  };
  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Student', 'Amount', 'Method', 'Notes'];
    const rows = payments.map(p => {
      const paidAt = p.paidAt instanceof Timestamp ? p.paidAt.toDate() : new Date(p.paidAt);
      return [paidAt.toLocaleDateString(), p.studentName, `$${p.amount.toFixed(2)}`, p.method, p.notes || ''];
    });
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `income-tracker-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };
  // Custom tooltip for charts
  const CustomTooltip = ({
    active,
    payload,
    label
  }) => {
    if (active && payload && payload.length) {
      return <div className={`p-3 rounded-lg shadow-lg border ${isDark ? 'bg-navy-800 border-navy-700 text-white' : 'bg-white border-gray-200 text-gray-800'}`} data-id="element-6842">
          <p className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6843">
            {label}
          </p>
          <p className="text-lg font-bold text-emerald-500" data-id="element-6844">
            ${payload[0].value.toFixed(2)}
          </p>
        </div>;
    }
    return null;
  };
  // Get unique students for filter
  const studentFilterOptions = ['All', ...students.map(s => s.name)];
  return <>
      <div className="mx-auto px-4 py-6" data-id="element-6845">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6" data-id="element-6846">
          <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6847">
            Income Tracker
          </h1>
          <button onClick={() => {
          setEditingPayment(null);
          setIsAddPaymentModalOpen(true);
        }} className={`mt-4 sm:mt-0 flex items-center justify-center gap-2 px-4 py-2 border-2 rounded-lg font-medium transition-colors ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10'}`} data-id="element-6848">
            <span data-id="element-6849">Add Payment</span>
          </button>
        </div>

        {/* Filters */}
        <div className={`p-4 rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm mb-6`} data-id="element-6850">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6851">
            <div data-id="element-6852">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6853">
                Date Range
              </label>
              <select value={dateRange} onChange={e => setDateRange(e.target.value as 'This month' | 'Last 30 days' | 'Year to date')} className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-800 focus:ring-deep-pink focus:border-deep-pink'} transition-colors`} data-id="element-6854">
                <option data-id="element-6855">This month</option>
                <option data-id="element-6856">Last 30 days</option>
                <option data-id="element-6857">Year to date</option>
              </select>
            </div>
            <div data-id="element-6858">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6859">
                Student
              </label>
              <select value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-800 focus:ring-deep-pink focus:border-deep-pink'} transition-colors`} data-id="element-6860">
                {studentFilterOptions.map(student => <option key={student} data-id="element-6861">{student}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6" data-id="element-6862">
          <StatCard icon={<DollarSignIcon className="h-6 w-6 text-green-500" data-id="element-6864" />} label="This Month" value={`$${thisMonthKPI.total.toFixed(2)}`} darkMode={isDark} data-id="element-6863" />
          <StatCard icon={<TrendingUpIcon className="h-6 w-6 text-blue-500" data-id="element-6866" />} label="Last 30 Days" value={`$${last30DaysKPI.total.toFixed(2)}`} darkMode={isDark} data-id="element-6865" />
          <StatCard icon={<CalendarIcon className="h-6 w-6 text-purple-500" data-id="element-6868" />} label="Year to Date" value={`$${yearToDateKPI.total.toFixed(2)}`} darkMode={isDark} data-id="element-6867" />
        </div>

        {/* Revenue Chart */}
        {revenueMonthlyData.length > 0 && <div className={`p-6 rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm mb-6`} data-id="element-6869">
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6870">
              Revenue Over Time
            </h3>
            <ResponsiveContainer width="100%" height={300} data-id="element-6871">
              <LineChart data={revenueMonthlyData} data-id="element-6872">
                <defs data-id="element-6873">
                  <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-6874">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.8} data-id="element-6875" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.3} data-id="element-6876" />
                  </linearGradient>
                  <filter id="glow" data-id="element-6877">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur" data-id="element-6878" />
                    <feMerge data-id="element-6879">
                      <feMergeNode in="coloredBlur" data-id="element-6880" />
                      <feMergeNode in="SourceGraphic" data-id="element-6881" />
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={0.5} data-id="element-6882" />
                <XAxis dataKey="month" stroke={isDark ? '#9ca3af' : '#6b7280'} tick={{
              fill: isDark ? '#9ca3af' : '#6b7280',
              fontSize: 12
            }} tickLine={{
              stroke: isDark ? '#4b5563' : '#d1d5db'
            }} data-id="element-6883" />
                <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} tick={{
              fill: isDark ? '#9ca3af' : '#6b7280',
              fontSize: 12
            }} tickLine={{
              stroke: isDark ? '#4b5563' : '#d1d5db'
            }} tickFormatter={value => `$${value}`} data-id="element-6884" />
                <Tooltip content={<CustomTooltip data-id="element-6886" />} cursor={{
              stroke: isDark ? '#4b5563' : '#d1d5db',
              strokeWidth: 1
            }} data-id="element-6885" />
                <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={revenueMonthlyData.length === 2 ? 2 : 3} strokeDasharray={revenueMonthlyData.length === 2 ? '0' : undefined} dot={props => {
              const {
                cx,
                cy,
                payload
              } = props;
              // Hide dot for baseline point (empty month)
              if (payload.month === '') return null;
              // Enhanced dot for single data point
              const isSinglePoint = revenueMonthlyData.length === 2;
              return <circle cx={cx} cy={cy} r={isSinglePoint ? 6 : 4} fill="#10b981" stroke={isDark ? '#1f2937' : '#ffffff'} strokeWidth={2} filter={isSinglePoint ? 'url(#glow)' : undefined} style={{
                filter: isSinglePoint ? 'drop-shadow(0 0 6px rgba(16, 185, 129, 0.6))' : undefined
              }} data-id="element-6888" />;
            }} activeDot={{
              r: 6,
              fill: '#10b981',
              stroke: isDark ? '#1f2937' : '#ffffff',
              strokeWidth: 2
            }} name="Revenue" data-id="element-6887" />
              </LineChart>
            </ResponsiveContainer>
          </div>}

        {/* Export Button */}
        <div className="mb-6" data-id="element-6889">
          <button onClick={exportToCSV} disabled={payments.length === 0} className={`flex items-center gap-2 px-4 py-2 rounded-lg ${isDark ? 'bg-navy-800 hover:bg-navy-700' : 'bg-white hover:bg-gray-50'} shadow-sm transition-colors ${payments.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-6890">
            <DownloadIcon className="h-5 w-5 text-deep-pink" data-id="element-6891" />
            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6892">
              Export CSV
            </span>
          </button>
        </div>

        {/* Payment History Table */}
        <div className={`p-6 rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-6893">
          <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6894">
            Payment History
          </h3>

          <div className="overflow-x-auto" data-id="element-6895">
            {loading ? <div className="py-12 text-center" data-id="element-6896">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto" data-id="element-6897"></div>
              </div> : payments.length > 0 ? <table className="w-full" data-id="element-6898">
                <thead data-id="element-6899">
                  <tr className={`border-b ${isDark ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-6900">
                    <th className={`text-left py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6901">
                      Date
                    </th>
                    <th className={`text-left py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6902">
                      Student
                    </th>
                    <th className={`text-left py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6903">
                      Amount
                    </th>
                    <th className={`text-left py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6904">
                      Method
                    </th>
                    <th className={`text-left py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6905">
                      Notes
                    </th>
                    <th className={`text-right py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6906">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-6907">
                  {payments.map(payment => {
                const paidAt = payment.paidAt instanceof Timestamp ? payment.paidAt.toDate() : new Date(payment.paidAt);
                return <tr key={payment.id} className={`border-b ${isDark ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-6908">
                        <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6909">
                          {paidAt.toLocaleDateString()}
                        </td>
                        <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6910">
                          {payment.studentName}
                        </td>
                        <td className={`py-3 px-4 font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`} data-id="element-6911">
                          ${payment.amount.toFixed(2)}
                        </td>
                        <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6912">
                          {payment.method}
                        </td>
                        <td className={`py-3 px-4 ${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`} data-id="element-6913">
                          {payment.notes || '-'}
                        </td>
                        <td className="py-3 px-4 text-right" data-id="element-6914">
                          <div className="flex items-center justify-end gap-2" data-id="element-6915">
                            <button onClick={() => handleEditPayment(payment)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-100'}`} title="Edit payment" data-id="element-6916">
                              <EditIcon className="h-4 w-4 text-blue-500" data-id="element-6917" />
                            </button>
                            <button onClick={() => handleDeletePayment(payment.id!)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-100'}`} title="Delete payment" data-id="element-6918">
                              <TrashIcon className="h-4 w-4 text-red-500" data-id="element-6919" />
                            </button>
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table> : <div className="py-12 text-center" data-id="element-6920">
                <DollarSignIcon className={`h-12 w-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-6921" />
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6922">
                  No payments recorded yet
                </p>
              </div>}
          </div>
        </div>
      </div>

      {/* Add/Edit Payment Modal */}
      <AddPaymentModal isOpen={isAddPaymentModalOpen} onClose={() => {
      setIsAddPaymentModalOpen(false);
      setEditingPayment(null);
    }} teacherId={currentUser?.uid || ''} editPayment={editingPayment} onSuccess={() => {
      loadPayments();
      setEditingPayment(null);
    }} data-id="element-6923" />
    </>;
};